import React from "react";
import { motion, HTMLMotionProps } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { logAnalyticsEvent } from "../lib/firebase";

interface DiceState {
  value: number;
  isRolling: boolean;
}

const useDice = (initialValue: number = 1) => {
  const [state, setState] = React.useState<DiceState>({
    value: initialValue,
    isRolling: false,
  });

  const roll = React.useCallback(() => {
    if (state.isRolling) return;

    setState((prev) => ({ ...prev, isRolling: true }));

    // サイコロを振るアニメーション中にランダムな数値を表示
    const rollInterval = setInterval(() => {
      setState((prev) => ({
        ...prev,
        value: Math.floor(Math.random() * 6) + 1,
      }));
    }, 50);

    // 1秒後にアニメーションを停止
    setTimeout(() => {
      clearInterval(rollInterval);
      setState((prev) => ({
        value: Math.floor(Math.random() * 6) + 1,
        isRolling: false,
      }));
    }, 1000);
  }, [state.isRolling]);

  return { state, roll };
};

const Dice: React.FC<{ state: DiceState; onClick: () => void }> = ({
  state,
  onClick,
}) => {
  return (
    <motion.div
      className="cursor-pointer"
      onClick={onClick}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      animate={{
        rotate: state.isRolling ? [0, 360] : 0,
      }}
      transition={{
        duration: state.isRolling ? 1 : 0.2,
        repeat: state.isRolling ? Infinity : 0,
      }}
    >
      <img
        src={`/images/dice${state.value}.png`}
        alt={`サイコロの目${state.value}`}
        className="w-20 h-20 object-contain"
        width="80"
        height="80"
        loading="eager"
      />
    </motion.div>
  );
};

const DiceGroup: React.FC = () => {
  const dice1 = useDice(1);
  const dice2 = useDice(2);
  const dice3 = useDice(3);

  const rollAll = React.useCallback(() => {
    dice1.roll();
    dice2.roll();
    dice3.roll();
  }, [dice1, dice2, dice3]);

  return (
    <div className="flex justify-center items-center gap-4 mb-8">
      <Dice state={dice1.state} onClick={rollAll} />
      <Dice state={dice2.state} onClick={rollAll} />
      <Dice state={dice3.state} onClick={rollAll} />
    </div>
  );
};

const MotionImage = motion.img as React.FC<HTMLMotionProps<"img">>;

const Hero: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const handleAppStoreClick = () => {
    logAnalyticsEvent("app_store_click", {
      platform: "ios",
      location: "hero_section",
    });
  };

  const handlePlayStoreClick = () => {
    logAnalyticsEvent("play_store_click", {
      platform: "android",
      location: "hero_section",
    });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden"
    >
      {/* 背景の畳模様 */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-10"
        style={{ backgroundImage: "url(/images/tatami.png)" }}
        role="presentation"
        aria-hidden="true"
      />

      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.6 }}
        className="container text-center relative z-10"
      >
        <MotionImage
          src="/images/icon.png"
          alt="本気のチンチロ アイコン"
          className="w-32 h-32 mx-auto mb-6"
          width={128}
          height={128}
          loading="eager"
          initial={{ scale: 0.8, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />

        <DiceGroup />

        <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-accent to-purple-500 bg-clip-text text-transparent">
          本気のチンチロ
        </h1>
        <p className="text-xl md:text-2xl mb-12 text-gray-300" role="doc-subtitle">
          本格的なチンチロゲームを、いつでもどこでも。
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4 items-center">
          <a
            href="https://apps.apple.com/jp/app/%E6%9C%AC%E6%B0%97%E3%81%AE%E3%83%81%E3%83%B3%E3%83%81%E3%83%AD/id6474092630"
            className="transition-transform hover:scale-105"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleAppStoreClick}
          >
            <img
              src="/images/app-store-badge.svg"
              alt="App Storeでダウンロード"
              className="h-14 w-auto"
              height="56"
              loading="eager"
            />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.kotaroyamazaki.chinchiro_app"
            className="transition-transform hover:scale-105"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handlePlayStoreClick}
          >
            <img
              src="/images/google_play_dl_button.png"
              alt="Google Playでダウンロード"
              className="h-14 w-auto"
              height="56"
              loading="eager"
            />
          </a>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
